import { useCallback, useEffect, useState } from 'react';
import { Button, DotLoading, Empty, List, Tag, Toast } from 'antd-mobile';
import { LeftOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '@ubus/shared';
import { useAuth } from '../hooks/useAuth';
import {
  fetchDriverReminderInbox,
  markDriverReminderRead,
  type DriverReminderInboxRow,
} from '../utils/reminderInbox';

function ReminderInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<DriverReminderInboxRow[]>([]);
  const [unreadIds, setUnreadIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!driverId) {
      setRows([]);
      setUnreadIds([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { rows: next, unreadIds: ids } = await fetchDriverReminderInbox(driverId);
      setRows(next);
      setUnreadIds(ids);
    } catch (err) {
      console.error('[ReminderInbox]', err);
      Toast.show({
        content: err instanceof Error ? err.message : t('reminders.loadFailed'),
      });
      setRows([]);
      setUnreadIds([]);
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const markRead = async (eventId: string) => {
    if (!driverId || busy) return;
    setBusy(true);
    try {
      await markDriverReminderRead(driverId, [eventId]);
      setUnreadIds((prev) => prev.filter((id) => id !== eventId));
    } catch (err) {
      Toast.show({
        content: err instanceof Error ? err.message : t('reminders.markFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    if (!driverId || unreadIds.length === 0 || busy) return;
    setBusy(true);
    try {
      await markDriverReminderRead(driverId, unreadIds);
      setUnreadIds([]);
      Toast.show({ content: t('reminders.markedAllRead') });
    } catch (err) {
      Toast.show({
        content: err instanceof Error ? err.message : t('reminders.markFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="driver-detail">
      <header className="driver-detail-nav">
        <button
          type="button"
          className="driver-detail-nav-back"
          onClick={() => navigate('/profile')}
          aria-label={t('common.back')}
        >
          <LeftOutline fontSize={22} />
        </button>
        <h1 className="driver-detail-nav-title">{t('reminders.title')}</h1>
        <span className="driver-detail-nav-spacer" aria-hidden="true" />
      </header>

      <div className="driver-section" style={{ padding: '8px 12px 24px' }}>
        {unreadIds.length > 0 ? (
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="mini" color="primary" fill="outline" disabled={busy} onClick={() => void markAllRead()}>
              {t('reminders.markAllRead')}
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <DotLoading color="primary" />
          </div>
        ) : rows.length === 0 ? (
          <Empty description={t('reminders.empty')} />
        ) : (
          <List>
            {rows.map((item) => {
              const unread = unreadIds.includes(item.id);
              return (
                <List.Item
                  key={item.id}
                  description={
                    <div style={{ marginTop: 4 }}>
                      <div>
                        {t('reminders.due')}: {item.due_date}
                        {item.ahead_days != null ? ` (−${item.ahead_days}d)` : ''}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 12 }}>
                        {formatDateTime(item.created_at)}
                      </div>
                    </div>
                  }
                  extra={
                    unread ? (
                      <Button
                        size="mini"
                        color="primary"
                        fill="none"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void markRead(item.id);
                        }}
                      >
                        {t('reminders.markRead')}
                      </Button>
                    ) : (
                      <Tag color="default" fill="outline">
                        {t('reminders.read')}
                      </Tag>
                    )
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {unread ? (
                      <Tag color="primary" fill="outline">
                        {t('reminders.unread')}
                      </Tag>
                    ) : null}
                    {t(`reminders.types.${item.type_code}`, { defaultValue: item.type_code })}
                  </span>
                </List.Item>
              );
            })}
          </List>
        )}
      </div>
    </div>
  );
}

export default ReminderInbox;
